import type { ContractStatusEnum, WorkOrderStatusEnum, BimWorkOrderOtherPriceData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { getCheckedIdsArr } from 'utils';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useGetBimWorkOrderDataFromProject } from '../../hooks';
import { BimOtherMenuRenderer } from '../cellRenderers';

interface IUseWorkOrderOtherMenuColumn {
    constructionTypeId?: number;
    refreshWorkOrderOtherPriceList: () => void;
    statusWorkOrder: WorkOrderStatusEnum;
    isFrameContract: boolean;
    contractStatus?: ContractStatusEnum;
    isCheckDisabled: boolean;
    orderRules?: IUseAccess['orderBim'];
}

export const useWorkOrderOtherMenuColumn = (props: IUseWorkOrderOtherMenuColumn) => {
    const {
        constructionTypeId,
        refreshWorkOrderOtherPriceList,
        statusWorkOrder,
        isFrameContract,
        contractStatus,
        isCheckDisabled,
        orderRules,
    } = props;
    const [checkedRows, setCheckedRows] = useState({});
    const [totalDetailsOpened, setTotalDetailsOpened] = useState(false);
    const [isSingleDeleteModalOpened, setIsSingleDeleteModalOpened] = useState(false);

    const { hasOtherActsKc2 } = useGetBimWorkOrderDataFromProject({});
    const { versionId } = useParams();
    const isVersionPage = !!versionId;

    const checkedRowsArr = getCheckedIdsArr(checkedRows);
    const resetCheckedRows = useCallback(() => {
        setCheckedRows({});
    }, []);

    const checkRowHandler = useCallback(({ id, state }) => {
        setCheckedRows((prev) => ({ ...prev, [id]: state }));
    }, []);

    const openSingleDeleteModalHandler = useCallback(() => {
        setIsSingleDeleteModalOpened(true);
    }, []);

    const closeSingleDeleteModalHandler = useCallback(() => {
        setIsSingleDeleteModalOpened(false);
        setCheckedRows({});
    }, []);

    const toggleTotalDetails = useCallback(() => {
        setTotalDetailsOpened((prev) => !prev);
    }, []);

    const menuColumn: ColDef<BimWorkOrderOtherPriceData> = useMemo(
        () => ({
            headerName: '',
            field: 'menu' as any,
            width: 90,
            cellRenderer: BimOtherMenuRenderer({
                constructionTypeId,
                checkedRows,
                statusWorkOrder,
                checkRowHandler,
                openSingleDeleteModalHandler,
                refreshWorkOrderOtherPriceList,
                toggleTotalDetails,
                isFrameContract,
                hasOtherActsKc2,
                contractStatus,
                isCheckDisabled,
                orderRules,
                isVersionPage,
            }),
            cellClassRules: {
                centeredAgGridCell: () => true,
            },
            resizable: false,
            pinned: 'left',
            lockPosition: 'left',
        }),
        [
            checkRowHandler,
            checkedRows,
            constructionTypeId,
            contractStatus,
            hasOtherActsKc2,
            isCheckDisabled,
            isFrameContract,
            isVersionPage,
            openSingleDeleteModalHandler,
            orderRules,
            refreshWorkOrderOtherPriceList,
            statusWorkOrder,
            toggleTotalDetails,
        ],
    );

    return {
        menuColumn,
        checkedRowsArr,
        resetCheckedRows,
        isSingleDeleteModalOpened,
        closeSingleDeleteModalHandler,
        totalDetailsOpened,
        toggleTotalDetails,
    };
};
